import { useNavigate } from 'react-router-dom';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { paths } from 'src/routes/routes/paths';

import { useSetRole } from 'src/hooks/useHandleSessions';

import { get, post } from '../http';

// --- API THUNKS START ---

export const getSidebarItemCountData = createAsyncThunk(
  'static/sidebar/count',
  async ({ enqueueSnackbar, userType }) => {
    try {
      const response = await get(`/static/dashboard/counts?user_type=${userType && userType}`);
      return response;
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message, { variant: 'error' });
      throw error?.response?.data?.message;
    }
  }
);

export const getAmazonConnectLink = createAsyncThunk(
  'amazon/connection/link',
  async ({ enqueueSnackbar, region, navigate }) => {
    try {
      const response = await post(`/advertiser-account/user/connect`, { region });

      if (response) {
        enqueueSnackbar('Updated Successfully');
        const redirectUrl = response?.redirectUrl;
        if (redirectUrl) {
          if (redirectUrl.startsWith('http')) {
            window.location.href = redirectUrl;
          } else {
            useNavigate()(redirectUrl);
          }
        }
      }
      return response;
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message, { variant: 'error' });
      throw error?.response?.data?.message;
    }
  }
);

export const getDashboardChartData = createAsyncThunk(
  'campaign-chart/member',
  async ({ enqueueSnackbar, groupBy, campaign, dates, selectedSort, selectedSort2 }) => {
    try {
      const response = await get(
        `/analytics/advertiser/graph?groupBy=${groupBy || ''}&campaignId=${campaign || ''}&from=${
          dates?.from || ''
        }&to=${dates?.to || ''}&value1=${selectedSort || ''}&value2=${selectedSort2 || ''}`
      );
      return response;
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message, { variant: 'error' });
      throw error?.response?.data?.message;
    }
  }
);

export const getIncomeChartData = createAsyncThunk(
  'income-chart/member',
  async ({ enqueueSnackbar, year }) => {
    try {
      const response = await get(`/wallet-transaction/member/income/graph`);
      return response;
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message, { variant: 'error' });
      throw error?.response?.data?.message;
    }
  }
);

export const getPartnerBalanceChartData = createAsyncThunk(
  'earning/admin/increment',
  async ({ enqueueSnackbar, year }) => {
    try {
      const response = await get(`/earning/admin/increment/graph/monthly?&year=${year || ''}`);
      return response;
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message, { variant: 'error' });
      throw error?.response?.data?.message;
    }
  }
);

export const getProgressionGraphData = createAsyncThunk(
  'school-course/school-user/graph/data',
  async ({ enqueueSnackbar }) => {
    try {
      const response = await get('/school-course/school-user/graph/data');
      return response;
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message, { variant: 'error' });
      throw error?.response?.data?.message;
    }
  }
);

export const getAmazonDataFetching = createAsyncThunk(
  'amazon/data/fetching',
  async ({ enqueueSnackbar, code, navigate }) => {
    try {
      const response = await post(`/advertiser-account/user/new/${code}`);
      console.log(response);
      if (response) {
        navigate(paths?.dashboard?.root);
        useSetRole(response?.user?.role, response?.user?.role);
      }
      return response;
    } catch (error) {
      console.log(error);
      if (error?.response?.data) {
        navigate(paths?.dashboard?.root);
      }
      enqueueSnackbar(error?.response?.data?.message, { variant: 'error' });
      throw error?.response?.data?.message;
    }
  }
);

// --- REDUX SLICE START ---

export const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState: {
    error: {},
    loading: false,
    chartLoading: false,
    dashboardData: {},
    campaignChart: [],
    incomeChart: [],
    balanceChart: [],
    progressionData: [],
    sidebarCounts: {},
    siteUrl: {},
    fetchAmazonDatas: {},
  },
  reducers: {
    clearDashboardError: (state) => {
      state.error = true;
    },
  },
  extraReducers: {
    [getSidebarItemCountData.pending]: (state) => {
      state.loading = true;
    },
    [getSidebarItemCountData.fulfilled]: (state, action) => {
      state.loading = false;
      state.sidebarCounts = action.payload;
      state.error = {};
    },
    [getSidebarItemCountData.rejected]: (state, action) => {
      state.loading = false;
      state.error = action.error;
    },

    [getDashboardChartData.pending]: (state) => {
      state.chartLoading = true;
    },
    [getDashboardChartData.fulfilled]: (state, action) => {
      state.chartLoading = false;
      state.campaignChart = action.payload;
      state.error = {};
    },
    [getDashboardChartData.rejected]: (state, action) => {
      state.chartLoading = false;
      state.error = action.error;
    },
    [getIncomeChartData.pending]: (state) => {
      state.chartLoading = true;
    },
    [getIncomeChartData.fulfilled]: (state, action) => {
      state.chartLoading = false;
      state.incomeChart = action.payload;
      state.error = {};
    },
    [getIncomeChartData.rejected]: (state, action) => {
      state.chartLoading = false;
      state.error = action.error;
    },

    [getPartnerBalanceChartData.pending]: (state) => {
      state.chartLoading = true;
    },
    [getPartnerBalanceChartData.fulfilled]: (state, action) => {
      state.chartLoading = false;
      state.balanceChart = action.payload;
      state.error = {};
    },
    [getPartnerBalanceChartData.rejected]: (state, action) => {
      state.chartLoading = false;
      state.error = action.error;
    },

    [getProgressionGraphData.pending]: (state) => {
      state.chartLoading = true;
    },
    [getProgressionGraphData.fulfilled]: (state, action) => {
      state.chartLoading = false;
      state.progressionData = action.payload;
      state.error = {};
    },
    [getProgressionGraphData.rejected]: (state, action) => {
      state.chartLoading = false;
      state.error = action.error;
    },

    [getAmazonConnectLink.pending]: (state) => {
      state.loading = true;
    },
    [getAmazonConnectLink.fulfilled]: (state, action) => {
      state.siteUrl = action.payload;
      state.error = {};
    },
    [getAmazonConnectLink.rejected]: (state, action) => {
      state.loading = false;
      state.error = action.error;
    },

    [getAmazonDataFetching.pending]: (state) => {
      state.loading = true;
    },
    [getAmazonDataFetching.fulfilled]: (state, action) => {
      state.fetchAmazonDatas = action.payload;
      state.error = {};
      state.loading = false;
    },
    [getAmazonDataFetching.rejected]: (state, action) => {
      state.loading = false;
      state.error = action.error;
    },
  },
});
export default dashboardSlice.reducer;
